<template>
  <div class="cm-contenedor">

    <!-- ━━━ ENCABEZADO ━━━ -->
    <div class="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
      <div>
        <h4 class="cm-titulo mb-1">
          <i class="bi bi-truck me-2"></i>Carga Masiva de Proveedores
        </h4>
        <p class="cm-subtitulo mb-0">Importa proveedores desde un archivo Excel (.xlsx / .xls)</p>
      </div>
      <a :href="urlPlantilla" download="plantilla_proveedores.xlsx" class="btn btn-outline-primary btn-sm">
        <i class="bi bi-download me-1"></i>Descargar plantilla
      </a>
    </div>

    <!-- ━━━ PASO 1: Seleccionar archivo y mapear columnas ━━━ -->
    <div class="cm-seccion mb-4">
      <div class="cm-seccion__cabecera">
        <i class="bi bi-1-circle-fill me-2"></i>Seleccionar archivo y mapear columnas
      </div>
      <div class="cm-seccion__cuerpo">

        <div class="row g-3 mb-3">
          <div class="col-md-6">
            <label class="form-label fw-semibold">Archivo Excel <span class="text-danger">*</span></label>
            <input
              ref="inputArchivo"
              type="file"
              class="form-control"
              accept=".xlsx,.xls"
              @change="onArchivoSeleccionado"
            />
            <div class="form-text cm-texto-secundario">Formatos aceptados: .xlsx y .xls</div>
          </div>
          <div class="col-md-6">
            <label class="form-label fw-semibold">Acción si el proveedor ya existe</label>
            <select class="form-select" v-model="accionDuplicado">
              <option value="actualizar">Actualizar datos (nombre, dirección, teléfono, correo)</option>
              <option value="solo_crear">Crear solo nuevos (ignorar duplicados)</option>
              <option value="ignorar">Ignorar completamente</option>
            </select>
          </div>
        </div>

        <!-- Mapeo de columnas -->
        <div v-if="columnasArchivo.length > 0">
          <div class="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <span class="fw-semibold cm-texto-primario">
              <i class="bi bi-arrow-left-right me-1"></i>Mapeo de columnas
            </span>
            <span class="cm-badge-primary">{{ columnasArchivo.length }} columnas detectadas</span>
          </div>
          <div class="row g-2 mb-3">
            <div v-for="campo in camposMapeo" :key="campo.key" class="col-xl-2 col-lg-3 col-md-4 col-sm-6">
              <label class="form-label small fw-semibold mb-1">
                {{ campo.etiqueta }}
                <span v-if="campo.requerido" class="text-danger">*</span>
                <span v-else class="cm-texto-muted" style="font-weight:400"> (opc.)</span>
              </label>
              <select class="form-select form-select-sm" v-model="mapeoColumnas[campo.key]">
                <option value="">— No mapear —</option>
                <option v-for="col in columnasArchivo" :key="col" :value="col">{{ col }}</option>
              </select>
            </div>
          </div>

          <div class="d-flex gap-2 align-items-center flex-wrap">
            <button
              class="btn btn-primary"
              @click="aplicarMapeo"
              :disabled="!mapeoValido || procesandoArchivo"
            >
              <span v-if="procesandoArchivo" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="bi bi-check2-circle me-2"></i>
              Aplicar mapeo y previsualizar
            </button>
            <button v-if="filas.length > 0" class="btn btn-outline-secondary btn-sm" @click="limpiarTodo">
              <i class="bi bi-x-circle me-1"></i>Limpiar
            </button>
            <span v-if="!mapeoValido" class="small cm-texto-error">
              <i class="bi bi-exclamation-triangle me-1"></i>Complete los campos obligatorios (*)
            </span>
          </div>
        </div>

        <!-- Estado vacío -->
        <div v-else class="cm-estado-vacio">
          <i class="bi bi-truck cm-estado-vacio__icono"></i>
          <p class="mb-0">Seleccione un archivo para comenzar</p>
        </div>

      </div>
    </div>

    <!-- ━━━ PASO 2: Vista previa ━━━ -->
    <div v-if="filas.length > 0" class="cm-seccion mb-4">
      <div class="cm-seccion__cabecera d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>
          <i class="bi bi-2-circle-fill me-2"></i>
          Vista previa —
          <strong>{{ filas.length }}</strong> proveedores
          <span v-if="filasConError > 0" class="badge bg-danger ms-2">{{ filasConError }} con error</span>
          <span v-if="filasValidas > 0" class="cm-badge-exito ms-2">{{ filasValidas }} válidos</span>
        </span>
        <div class="form-check form-switch mb-0">
          <input class="form-check-input" type="checkbox" id="soloErroresProv" v-model="soloMostrarErrores" />
          <label class="form-check-label small cm-texto-secundario" for="soloErroresProv">Solo errores</label>
        </div>
      </div>

      <div class="table-responsive" style="max-height:520px;overflow-y:auto">
        <table class="cm-tabla">
          <thead>
            <tr>
              <th style="width:36px">#</th>
              <th>Identificación</th>
              <th>Tipo</th>
              <th>Razón Social</th>
              <th>Teléfono</th>
              <th>Correo</th>
              <th>Dirección</th>
              <th style="width:90px">Estado</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(fila, idx) in filasMostradas"
              :key="idx"
              :class="claseFila(fila)"
            >
              <td class="cm-celda-muted">{{ fila._numero }}</td>
              <td>
                <code class="cm-codigo">{{ fila.identificacion }}</code>
                <span v-if="fila._existe" class="cm-badge-aviso ms-1" title="Ya existe en el sistema">
                  <i class="bi bi-arrow-repeat"></i>
                </span>
              </td>
              <td>
                <span class="badge bg-secondary">{{ etiquetaTipo(fila.tipo_identificacion) }}</span>
              </td>
              <td class="cm-celda-nombre" :title="fila.razon_social">{{ fila.razon_social }}</td>
              <td class="cm-celda-muted">{{ fila.telefono || '—' }}</td>
              <td class="cm-celda-muted">{{ fila.correo || '—' }}</td>
              <td class="cm-celda-nombre cm-celda-muted" :title="fila.direccion">{{ truncar(fila.direccion) }}</td>
              <td>
                <div v-if="fila._errores.length > 0">
                  <span class="badge bg-danger cm-badge-estado" :title="fila._errores.join(', ')">
                    <i class="bi bi-x-circle me-1"></i>Error
                  </span>
                  <div v-for="e in fila._errores" :key="e" class="small text-danger mt-1 cm-msg-estado">{{ e }}</div>
                </div>
                <div v-else-if="fila._advertencias.length > 0">
                  <span class="cm-badge-aviso cm-badge-estado" :title="fila._advertencias.join(', ')">
                    <i class="bi bi-exclamation-triangle me-1"></i>Aviso
                  </span>
                  <div v-for="a in fila._advertencias" :key="a" class="small mt-1 cm-msg-estado cm-texto-aviso">{{ a }}</div>
                </div>
                <span v-else class="cm-badge-exito cm-badge-estado">
                  <i class="bi bi-check-circle me-1"></i>OK
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ━━━ PASO 3: Importar ━━━ -->
    <div v-if="filasValidas > 0" class="cm-seccion mb-4">
      <div class="cm-seccion__cabecera">
        <i class="bi bi-3-circle-fill me-2"></i>Importar proveedores
      </div>
      <div class="cm-seccion__cuerpo">

        <!-- Barra de progreso -->
        <div v-if="importando" class="mb-3">
          <div class="d-flex justify-content-between mb-1">
            <small class="cm-texto-secundario">Importando {{ progresoActual }} de {{ totalImportar }}…</small>
            <small class="cm-texto-secundario">{{ Math.round(porcentajeProgreso) }}%</small>
          </div>
          <div class="progress cm-barra-progreso">
            <div
              class="progress-bar progress-bar-striped progress-bar-animated cm-barra-progreso__fill"
              :style="{ width: porcentajeProgreso + '%' }"
            ></div>
          </div>
        </div>

        <!-- Resumen -->
        <div v-if="resultadoImportacion" class="row g-3 mb-3">
          <div class="col-6 col-md-3">
            <div class="cm-resumen cm-resumen--exito">
              <div class="cm-resumen__num">{{ resultadoImportacion.creados }}</div>
              <div class="cm-resumen__label">Creados</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="cm-resumen cm-resumen--aviso">
              <div class="cm-resumen__num">{{ resultadoImportacion.actualizados }}</div>
              <div class="cm-resumen__label">Actualizados</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="cm-resumen cm-resumen--neutro">
              <div class="cm-resumen__num">{{ resultadoImportacion.ignorados }}</div>
              <div class="cm-resumen__label">Ignorados</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="cm-resumen cm-resumen--error">
              <div class="cm-resumen__num">{{ resultadoImportacion.fallidos }}</div>
              <div class="cm-resumen__label">Fallidos</div>
            </div>
          </div>
        </div>

        <!-- Errores de importación -->
        <div v-if="erroresImportacion.length > 0" class="alert alert-warning mb-3">
          <strong><i class="bi bi-exclamation-triangle me-1"></i>Proveedores no importados:</strong>
          <ul class="mb-0 mt-2 small">
            <li v-for="(err, i) in erroresImportacion" :key="i">
              <strong>{{ err.identificacion }}</strong>: {{ err.mensaje }}
            </li>
          </ul>
        </div>

        <!-- Alertas globales -->
        <div v-if="mensajeError" class="alert alert-danger d-flex align-items-center gap-2 mb-3" role="alert">
          <i class="bi bi-x-circle-fill fs-5 flex-shrink-0"></i>
          <div>{{ mensajeError }}</div>
          <button type="button" class="btn-close ms-auto" @click="mensajeError = ''"></button>
        </div>
        <div v-if="mensajeExito" class="alert alert-success d-flex align-items-center gap-2 mb-3" role="alert">
          <i class="bi bi-check-circle-fill fs-5 flex-shrink-0"></i>
          <div>{{ mensajeExito }}</div>
          <button type="button" class="btn-close ms-auto" @click="mensajeExito = ''"></button>
        </div>

        <div class="d-flex gap-2 flex-wrap">
          <button
            class="btn btn-primary px-4"
            :disabled="importando || importacionCompletada"
            @click="iniciarImportacion"
          >
            <span v-if="importando" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bi bi-cloud-upload me-2"></i>
            {{ importacionCompletada ? 'Importación completada' : `Importar ${filasValidas} proveedores` }}
          </button>
          <button v-if="importacionCompletada" class="btn btn-outline-secondary" @click="limpiarTodo">
            <i class="bi bi-arrow-repeat me-1"></i>Nueva importación
          </button>
          <router-link to="/personas/proveedores" class="btn btn-outline-primary">
            <i class="bi bi-truck me-1"></i>Ver proveedores
          </router-link>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import { supplierService } from '@/services/supplierService'

const TIPOS_IDENTIFICACION = {
  '04': 'RUC',
  '05': 'Cédula',
  '06': 'Pasaporte',
  '07': 'Cons. Final',
  '08': 'Id. Exterior',
}

const CAMPOS_MAPEO = [
  {
    key: 'identificacion',
    etiqueta: 'Identificación',
    requerido: true,
    aliasDefecto: ['identificacion', 'identificación', 'ruc', 'cedula', 'cédula', 'ci', 'ruc/ci', 'nit', 'pasaporte', 'numero', 'número', 'id', 'identification_number', 'identification number', 'número de identificación'],
  },
  {
    key: 'razon_social',
    etiqueta: 'Razón Social',
    requerido: true,
    aliasDefecto: ['razon social', 'razón social', 'nombre', 'business_name', 'business name', 'proveedor', 'razon_social', 'nombre proveedor', 'empresa', 'nombre completo'],
  },
  {
    key: 'tipo_identificacion',
    etiqueta: 'Tipo Ident.',
    requerido: false,
    aliasDefecto: ['tipo', 'tipo_identificacion', 'tipo identificacion', 'tipo identificación', 'tipo ident', 'type', 'tipo doc'],
  },
  {
    key: 'telefono',
    etiqueta: 'Teléfono',
    requerido: false,
    aliasDefecto: ['telefono', 'teléfono', 'tel', 'phone', 'celular', 'movil', 'móvil', 'contacto'],
  },
  {
    key: 'correo',
    etiqueta: 'Correo',
    requerido: false,
    aliasDefecto: ['correo', 'email', 'mail', 'e-mail', 'correo electrónico', 'correo electronico'],
  },
  {
    key: 'direccion',
    etiqueta: 'Dirección',
    requerido: false,
    aliasDefecto: ['direccion', 'dirección', 'address', 'domicilio', 'ubicacion', 'ubicación'],
  },
]

export default {
  name: 'ClientCargaMasivaProveedores',

  data() {
    return {
      archivoSeleccionado: null,
      datosRaw: [],
      columnasArchivo: [],
      mapeoColumnas: {},
      procesandoArchivo: false,

      accionDuplicado: 'actualizar',

      filas: [],
      camposMapeo: CAMPOS_MAPEO,
      soloMostrarErrores: false,

      importando: false,
      importacionCompletada: false,
      progresoActual: 0,
      totalImportar: 0,
      resultadoImportacion: null,
      erroresImportacion: [],

      mensajeError: '',
      mensajeExito: '',
    }
  },

  computed: {
    filasConError() {
      return this.filas.filter(f => f._errores.length > 0).length
    },
    filasValidas() {
      return this.filas.filter(f => f._errores.length === 0).length
    },
    filasMostradas() {
      return this.soloMostrarErrores
        ? this.filas.filter(f => f._errores.length > 0)
        : this.filas
    },
    porcentajeProgreso() {
      if (this.totalImportar === 0) return 0
      return (this.progresoActual / this.totalImportar) * 100
    },
    mapeoValido() {
      return CAMPOS_MAPEO.filter(c => c.requerido).every(c => !!this.mapeoColumnas[c.key])
    },
    urlPlantilla() {
      const encabezados = ['Identificación', 'Razón Social', 'Tipo (04=RUC/05=Cédula/06=Pasaporte)', 'Teléfono', 'Correo', 'Dirección']
      const ejemplos = [
        ['1791234560001', 'Proveedor Ejemplo S.A.', '04', '022334455', 'proveedor@ejemplo.com', 'Av. Industrial 789'],
        ['0912345678', 'Carlos Gómez', '05', '0987654321', 'carlos@correo.com', 'Calle Norte 321'],
      ]
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([encabezados, ...ejemplos])
      XLSX.utils.book_append_sheet(wb, ws, 'Proveedores')
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'base64' })
      return `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${wbout}`
    },
  },

  created() {
    this.inicializarMapeo()
  },

  methods: {
    inicializarMapeo() {
      const mapeo = {}
      CAMPOS_MAPEO.forEach(c => { mapeo[c.key] = '' })
      this.mapeoColumnas = mapeo
    },

    onArchivoSeleccionado(e) {
      const archivo = e.target.files[0]
      this.archivoSeleccionado = archivo || null
      this.filas = []
      this.datosRaw = []
      this.columnasArchivo = []
      this.inicializarMapeo()
      this.resultadoImportacion = null
      this.erroresImportacion = []
      this.importacionCompletada = false
      this.mensajeError = ''
      this.mensajeExito = ''
      if (!archivo) return
      this.leerColumnas(archivo)
    },

    async leerColumnas(archivo) {
      try {
        const buffer = await this.leerArchivoComoBuffer(archivo)
        const wb = XLSX.read(buffer, { type: 'array' })
        const hoja = wb.Sheets[wb.SheetNames[0]]
        const datos = XLSX.utils.sheet_to_json(hoja, { defval: '' })

        if (!datos || datos.length === 0) {
          this.mensajeError = 'El archivo está vacío o no tiene datos válidos.'
          return
        }

        this.datosRaw = datos
        this.columnasArchivo = Object.keys(datos[0])

        // Auto-mapeo inteligente por alias
        CAMPOS_MAPEO.forEach(campo => {
          const encontrada = this.columnasArchivo.find(col =>
            campo.aliasDefecto.includes(col.toLowerCase().trim())
          )
          this.mapeoColumnas[campo.key] = encontrada || ''
        })
      } catch {
        this.mensajeError = 'Error al leer el archivo. Verifique que sea un Excel válido (.xlsx / .xls).'
      }
    },

    async aplicarMapeo() {
      if (!this.mapeoValido || this.datosRaw.length === 0) return
      this.procesandoArchivo = true
      this.mensajeError = ''
      this.filas = []

      try {
        const existentes = await this.cargarIdentificacionesExistentes()

        const mapeo = this.mapeoColumnas
        const obtener = (row, key) => {
          const col = mapeo[key]
          return col ? String(row[col] ?? '').trim() : ''
        }

        this.filas = this.datosRaw.map((row, i) => {
          const identificacion = obtener(row, 'identificacion')
          const tipoRaw        = obtener(row, 'tipo_identificacion')
          const tipoDetectado  = (tipoRaw ? this.normalizarTipo(tipoRaw) : null) || this.detectarTipo(identificacion)

          const fila = {
            _numero: i + 2,
            identificacion,
            razon_social:        obtener(row, 'razon_social'),
            tipo_identificacion: tipoDetectado,
            telefono:            obtener(row, 'telefono'),
            correo:              obtener(row, 'correo'),
            direccion:           obtener(row, 'direccion'),
            _errores:            [],
            _advertencias:       [],
            _existe:             false,
            _idExistente:        null,
          }

          // Validaciones obligatorias
          if (!fila.identificacion) fila._errores.push('Identificación vacía')
          if (!fila.razon_social)   fila._errores.push('Razón social vacía')
          if (!fila.tipo_identificacion) fila._errores.push('No se pudo determinar el tipo de identificación')

          // Advertencias
          if (fila.correo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fila.correo)) {
            fila._advertencias.push('Correo con formato inválido')
          }

          // Marcar duplicados
          if (fila.identificacion && existentes[fila.identificacion] !== undefined) {
            fila._existe = true
            fila._idExistente = existentes[fila.identificacion]
          }

          return fila
        })
      } catch {
        this.mensajeError = 'Error al procesar el archivo.'
      } finally {
        this.procesandoArchivo = false
      }
    },

    async cargarIdentificacionesExistentes() {
      const mapa = {}
      let offset = 0
      const limite = 100
      let continuar = true

      while (continuar) {
        try {
          const res = await supplierService.getSuppliers({ limit: limite, offset, orden: 'id', dir: 'ASC' })
          const lista = res?.data || []
          lista.forEach(p => {
            mapa[p.identification_number] = p.id
          })
          if (lista.length < limite) continuar = false
          else offset += limite
        } catch {
          continuar = false
        }
      }

      return mapa
    },

    claseFila(fila) {
      if (fila._errores.length > 0) return 'cm-fila--error'
      if (fila._advertencias.length > 0) return 'cm-fila--aviso'
      if (fila._existe) return 'cm-fila--existente'
      return ''
    },

    async iniciarImportacion() {
      const filasAImportar = this.filas.filter(f => f._errores.length === 0)
      if (filasAImportar.length === 0) return

      this.importando = true
      this.progresoActual = 0
      this.totalImportar = filasAImportar.length
      this.resultadoImportacion = { creados: 0, actualizados: 0, ignorados: 0, fallidos: 0 }
      this.erroresImportacion = []
      this.mensajeError = ''
      this.mensajeExito = ''

      const LOTE = 10
      for (let i = 0; i < filasAImportar.length; i += LOTE) {
        const lote = filasAImportar.slice(i, i + LOTE)
        await Promise.all(lote.map(fila => this.importarFila(fila)))
        this.progresoActual = Math.min(i + LOTE, filasAImportar.length)
      }

      this.importando = false
      this.importacionCompletada = true
      const r = this.resultadoImportacion
      this.mensajeExito = `Importación completada: ${r.creados} creados, ${r.actualizados} actualizados, ${r.ignorados} ignorados, ${r.fallidos} fallidos.`
    },

    async importarFila(fila) {
      try {
        const payload = {
          identification_number: fila.identificacion,
          business_name:         fila.razon_social,
          tipo_identificacion:   fila.tipo_identificacion,
          phone:                 fila.telefono  || null,
          email:                 fila.correo    || null,
          address:               fila.direccion || null,
        }

        if (fila._existe && fila._idExistente) {
          if (this.accionDuplicado === 'solo_crear' || this.accionDuplicado === 'ignorar') {
            this.resultadoImportacion.ignorados++
            return
          }
          await supplierService.updateSupplier(fila._idExistente, payload)
          this.resultadoImportacion.actualizados++

        } else {
          // Proveedor nuevo (o existente sin ID previo)
          try {
            await supplierService.createSupplier(payload)
            this.resultadoImportacion.creados++
          } catch (err) {
            const status = err?.response?.status
            if (status === 409) {
              // El proveedor existe (el backend retorna 409 sin ID)
              this.resultadoImportacion.ignorados++
            } else {
              throw err
            }
          }
        }

      } catch (err) {
        this.resultadoImportacion.fallidos++
        this.erroresImportacion.push({
          identificacion: fila.identificacion,
          mensaje: err?.response?.data?.error || err?.response?.data?.message || err.message || 'Error desconocido',
        })
      }
    },

    // ── Utilidades ──
    detectarTipo(identificacion) {
      const limpia = String(identificacion || '').replace(/\s/g, '')
      if (limpia === '9999999999999') return '07'
      if (limpia.length === 13) return '04'
      if (limpia.length === 10) return '05'
      return '06'
    },

    normalizarTipo(valor) {
      const v = String(valor).trim().toLowerCase()
      const mapa = {
        '4': '04', '04': '04', 'ruc': '04',
        '5': '05', '05': '05', 'cedula': '05', 'cédula': '05', 'ci': '05',
        '6': '06', '06': '06', 'pasaporte': '06', 'passport': '06',
        '7': '07', '07': '07', 'consumidor': '07', 'consumidor final': '07',
        '8': '08', '08': '08', 'exterior': '08', 'id exterior': '08',
      }
      return mapa[v] || null
    },

    etiquetaTipo(codigo) {
      return TIPOS_IDENTIFICACION[codigo] || codigo || '—'
    },

    truncar(texto, max = 30) {
      if (!texto) return '—'
      return texto.length > max ? texto.substring(0, max) + '…' : texto
    },

    leerArchivoComoBuffer(archivo) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(new Uint8Array(e.target.result))
        reader.onerror = reject
        reader.readAsArrayBuffer(archivo)
      })
    },

    limpiarTodo() {
      this.archivoSeleccionado = null
      this.datosRaw = []
      this.filas = []
      this.columnasArchivo = []
      this.inicializarMapeo()
      this.resultadoImportacion = null
      this.erroresImportacion = []
      this.importacionCompletada = false
      this.mensajeError = ''
      this.mensajeExito = ''
      if (this.$refs.inputArchivo) this.$refs.inputArchivo.value = ''
    },
  },
}
</script>

<style scoped>
.cm-contenedor { padding: 1.5rem; color: var(--text-primary); }
.cm-titulo { font-size: 1.35rem; font-weight: 700; color: var(--text-primary); }
.cm-subtitulo { font-size: 0.875rem; color: var(--text-secondary); }

.cm-seccion {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}
.cm-seccion__cabecera {
  background: var(--primary-color, var(--primary-main, #0ea5e9));
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.6rem 1.1rem;
}
.cm-seccion__cuerpo { padding: 1.25rem; }

.cm-estado-vacio {
  text-align: center;
  padding: 2.5rem 1rem;
  color: var(--text-secondary);
}
.cm-estado-vacio__icono {
  font-size: 2.5rem;
  display: block;
  margin-bottom: 0.75rem;
  opacity: 0.4;
}

.cm-tabla { width: 100%; border-collapse: collapse; font-size: 0.825rem; }
.cm-tabla thead th {
  position: sticky; top: 0; z-index: 1;
  background: var(--bg-light, #f8fafc);
  border-bottom: 2px solid var(--border-main);
  padding: 0.5rem 0.6rem;
  font-weight: 600;
  white-space: nowrap;
  color: var(--text-primary);
}
.dark-theme .cm-tabla thead th { background: var(--bg-tertiary, #1c2535); }
.cm-tabla tbody td { padding: 0.45rem 0.6rem; border-bottom: 1px solid var(--border-main); vertical-align: middle; }
.cm-fila--error td    { background: rgba(239,68,68,.07) !important; }
.cm-fila--aviso td    { background: rgba(234,179,8,.07) !important; }
.cm-fila--existente td { background: rgba(14,165,233,.05) !important; }
.cm-celda-muted { color: var(--text-secondary); }
.cm-celda-nombre { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cm-codigo { font-size: 0.78rem; background: var(--bg-soft, #f0f4f8); padding: 0.1rem 0.3rem; border-radius: 3px; }

.cm-badge-primary { background: var(--primary-main, #0ea5e9); color: #fff; font-size: 0.75rem; padding: 0.2rem 0.5rem; border-radius: 12px; }
.cm-badge-exito   { background: #dcfce7; color: #166534; font-size: 0.75rem; padding: 0.2rem 0.5rem; border-radius: 12px; }
.cm-badge-aviso   { background: #fef9c3; color: #854d0e; font-size: 0.75rem; padding: 0.2rem 0.5rem; border-radius: 12px; }
.cm-badge-estado  { font-size: 0.75rem; }
.cm-msg-estado    { font-size: 0.75rem; line-height: 1.3; }
.cm-texto-primario   { color: var(--text-primary); }
.cm-texto-secundario { color: var(--text-secondary); }
.cm-texto-muted      { color: var(--text-muted, #94a3b8); }
.cm-texto-error      { color: #dc2626; }
.cm-texto-aviso      { color: #92400e; }

.cm-barra-progreso        { height: 10px; border-radius: 5px; background: var(--border-main); }
.cm-barra-progreso__fill  { background: var(--primary-main, #0ea5e9); }

.cm-resumen { border: 1px solid var(--border-main); border-radius: 8px; text-align: center; padding: 0.75rem; }
.cm-resumen__num   { font-size: 1.6rem; font-weight: 700; line-height: 1; }
.cm-resumen__label { font-size: 0.75rem; color: var(--text-secondary); margin-top: 0.25rem; }
.cm-resumen--exito .cm-resumen__num  { color: #16a34a; }
.cm-resumen--aviso .cm-resumen__num  { color: #d97706; }
.cm-resumen--neutro .cm-resumen__num { color: var(--text-secondary); }
.cm-resumen--error .cm-resumen__num  { color: #dc2626; }
</style>
